import { useCallback, useEffect, useState } from 'react';

import moment from 'moment';

import { CalendarAdornmentProps } from '../CalendarAdornment.props';

type UseRangeDatePickerProps = Pick<CalendarAdornmentProps, 'value' | 'type' | 'dateFormat' | 'onChange'>;

// The range value comes in two shapes:
//  - calendar selection: `${start} - ${end}` formatted with dateFormat (e.g. "01/05/2026 - 02/05/2026")
//  - keyboard/masked input: compact digits with no separators (e.g. "0105202602052026"),
//    where each date is dateFormat with all non-letter chars stripped (e.g. "MMDDYYYY").
const parseRangeValue = (
  value: CalendarAdornmentProps['value'],
  dateFormat?: string,
): [Date | undefined, Date | undefined] => {
  if (!value || typeof value !== 'string' || !dateFormat) {
    return [undefined, undefined];
  }

  let startStr: string;
  let endStr: string;
  let parseFormat = dateFormat;

  if (value.includes('-')) {
    [startStr = '', endStr = ''] = value.split(' - ');
  } else {
    // Compact keyboard format: split the raw digits into two equal-length dates.
    parseFormat = dateFormat.replace(/[^A-Za-z]/g, ''); // "MM/DD/YYYY" -> "MMDDYYYY"
    const perDateLength = parseFormat.length;

    startStr = value.slice(0, perDateLength);
    endStr = value.slice(perDateLength, perDateLength * 2);
  }

  const startMoment = startStr ? moment(startStr.trim(), parseFormat, true) : null;
  const endMoment = endStr ? moment(endStr.trim(), parseFormat, true) : null;

  return [
    startMoment?.isValid() ? startMoment.toDate() : undefined,
    endMoment?.isValid() ? endMoment.toDate() : undefined,
  ];
};

export const useRangeDatePicker = ({ value, type, dateFormat, onChange }: UseRangeDatePickerProps) => {
  const [initialStart, initialEnd] = parseRangeValue(value, dateFormat);

  const [startRangeDate, setStartRangeDate] = useState<Date>(initialStart ?? moment().toDate());
  const [endRangeDate, setEndRangeDate] = useState<Date>(initialEnd ?? moment().toDate());

  const handleRangeDateChange = useCallback(
    (dates?: Date[], callback?: () => void) => {
      if (!dates || !dateFormat) {
        return;
      }

      const [firstDate, secondDate] = dates;

      if (onChange) {
        onChange({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          target: {
            value: `${moment(firstDate || null).format(dateFormat)} - ${
              secondDate ? moment(secondDate).format(dateFormat) : ''
            }`,
          },
        });

        setStartRangeDate(firstDate);
        setEndRangeDate(secondDate);
      }

      if (!!firstDate && !!secondDate) {
        callback?.();
      }
    },
    [dateFormat, onChange],
  );

  // Sync internal range state from the incoming value so the calendar reflects
  // a default/preselected range as soon as it opens (not only after manual reselection).
  useEffect(() => {
    if (type !== 'date-range') {
      return;
    }

    const [start, end] = parseRangeValue(value, dateFormat);

    if (start) {
      setStartRangeDate(start);
    }

    if (end) {
      setEndRangeDate(end);
    }
  }, [value, dateFormat, type]);

  return {
    startRangeDate,
    endRangeDate,
    handleRangeDateChange,
  };
};
